// App flags lite (Console D, views/flags.js; APS-325, D103 #10): the app's own feature flags,
// read with setFeatureFlag and analysed, never configured. The list shows each flag's observed
// split (the share of exposed sessions per variant, not the provider's rollout), its exposed
// sessions and its crash-free sessions per variant, as dots on one scale shared by every row;
// one flag adds its issues per variant and the ones seen in one variant only. Left out until the
// product reads them: users per variant, first seen, the crash differential and performance by
// variant. Flags arrive as feature_flag.<name> on the session; names and variants are the app's
// data (translate="no").
import { useQueries, useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useState } from 'react'
import {
  alertRulesQuery,
  appsQuery,
  flagsQuery,
  issuesQuery,
  projectsQuery,
  windowReleasesQuery,
} from '../api/queries'
import type { FeatureFlags, IssueSummary } from '../api/types'
import {
  CodeTabs,
  EmptyNote,
  HovRow,
  KV,
  LK,
  PageHead,
  Section,
  Table,
  TableFoot,
} from '../components/d'
import { appLabel } from '../components/Shell'
import { ErrorState, Loading } from '../components/states'
import { type Translator, useI18n } from '../i18n'
import { issueTitle } from '../lib/issues'
import { sandboxFor } from '../sandbox'
import { useProjectParams } from './params'
import { carried, rangeDays, type ScopeSearch } from './search'

type Flag = FeatureFlags['flags'][number]

/** A variant as the page draws it; `other` sums the variants past the top ones. */
export interface VariantRow {
  key: string
  label: string
  sessions: number
  crashed: number
  /** Crash-free sessions, in percent; null without sessions. */
  crashFree: number | null
  other?: boolean
}

export function variantRows(t: Translator['t'], flag: Flag): VariantRow[] {
  const cf = (sessions: number, crashed: number) =>
    sessions > 0 ? (1 - crashed / sessions) * 100 : null
  return [
    ...flag.variants.map((v) => ({
      key: v.variant,
      label: v.variant,
      sessions: v.sessions,
      crashed: v.crashed_sessions,
      crashFree: cf(v.sessions, v.crashed_sessions),
    })),
    ...(flag.other
      ? [
          {
            key: '',
            label: t('flags.other', { count: flag.other.variants }),
            sessions: flag.other.sessions,
            crashed: flag.other.crashed_sessions,
            crashFree: cf(flag.other.sessions, flag.other.crashed_sessions),
            other: true,
          },
        ]
      : []),
  ]
}

/** The share of the flag's exposed sessions per variant, in percent (a session may count twice). */
export function shares(rows: VariantRow[]): number[] {
  const total = rows.reduce((n, r) => n + r.sessions, 0)
  return rows.map((r) => (total > 0 ? (r.sessions / total) * 100 : 0))
}

/** How far apart the variants' crash-free sessions are, in points; 0 with fewer than two. */
export function crashFreeGap(rows: VariantRow[]): number {
  const cfs = rows.flatMap((r) => (r.crashFree === null ? [] : [r.crashFree]))
  return cfs.length > 1 ? Math.max(...cfs) - Math.min(...cfs) : 0
}

/** A gap this wide (points) marks the lowest variant, as the mock does. */
const BAD_GAP = 0.3

/**
 * The crash-free scale every row of a page shares, in percent: the values (and the alert's
 * threshold, when there is one) with a margin, on tenths, never past 100.
 */
export function crashFreeScale(values: number[], threshold?: number): [number, number] {
  const all = threshold === undefined ? values : [...values, threshold]
  if (!all.length) return [99, 100]
  const lo = Math.max(0, Math.floor((Math.min(...all) - 0.1) * 10) / 10)
  const hi = Math.min(100, Math.ceil((Math.max(...all) + 0.1) * 10) / 10)
  return hi > lo ? [lo, hi] : [Math.max(0, hi - 0.1), hi]
}

const at = ([lo, hi]: [number, number], v: number) =>
  `${(((Math.min(hi, Math.max(lo, v)) - lo) / (hi - lo)) * 100).toFixed(1)}%`

const cfText = (v: number | null) => (v === null ? '—' : v.toFixed(2))

/** The crash-free threshold of the scope's enabled rule, if there is one (never a made-up 99.5). */
function useCrashFreeAlert(appId: string | undefined): number | undefined {
  const { orgId, projectId } = useProjectParams()
  const rules = useQuery({ ...alertRulesQuery(orgId, projectId), enabled: !sandboxFor(projectId) })
  const live = (rules.data?.rules ?? []).filter(
    (r) => r.enabled && r.preset === 'crash_free_below' && (!r.app_id || r.app_id === appId),
  )
  const rule = live.find((r) => r.app_id === appId) ?? live.find((r) => !r.app_id)
  const v = Number(rule?.params.threshold_percent)
  return rule && Number.isFinite(v) ? v : undefined
}

/** The scope in words: project · app · every release (flags have no release filter) · range. */
function useScopeLine(search: ScopeSearch, days: number): string {
  const { t } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const projects = useQuery(projectsQuery(orgId))
  const apps = useQuery(appsQuery(orgId, projectId))
  const app = apps.data?.find((a) => a.id === search.app)
  return [
    sandboxFor(projectId)?.projectName ?? projects.data?.find((p) => p.id === projectId)?.name,
    app ? appLabel(app) : t('shell.allAppsShort'),
    t('flags.meta.everyRelease'),
    days === 1 ? t('issues.chip.day') : t('issues.chip.days', { days }),
  ]
    .flatMap((part) => (part ? part.split(' · ') : []))
    .filter((part, i, all) => part !== all[i - 1])
    .join(' · ')
}

function useFlags() {
  const { projectId } = useProjectParams()
  const search = useSearch({ strict: false }) as ScopeSearch
  const days = rangeDays(search.range, 'flags')
  const flags = useQuery(flagsQuery(projectId, search.app, days))
  return { search, days, flags }
}

/** The observed split: one segment per variant, its width the share of exposed sessions. */
function Rollout({ rows, labels }: { rows: VariantRow[]; labels?: boolean }) {
  const { t } = useI18n()
  const share = shares(rows)
  const said = rows.map((r, i) => `${r.label} ${Math.round(share[i] ?? 0)} %`).join(' · ')
  return (
    <span className="ff-ro" role="img" aria-label={t('flags.splitLabel', { split: said })}>
      <span className="ff-rb">
        {rows.map((r, i) => (
          <i
            key={r.key || 'other'}
            className={stepClass(i, r)}
            style={{ flex: `${share[i]} 1 0` }}
          />
        ))}
      </span>
      {labels ? (
        <span className="ff-rl" aria-hidden="true" translate="no">
          {rows.map((r, i) => (
            <span key={r.key || 'other'} style={{ flex: `${share[i]} 1 0` }}>
              {r.label} <b>{Math.round(share[i] ?? 0)}</b>
            </span>
          ))}
        </span>
      ) : null}
    </span>
  )
}

/** Each variant keeps one ink step on a page; the summed rest is grey. */
const stepClass = (i: number, r: VariantRow) => (r.other ? 'ff-so' : `ff-s${Math.min(i, 2)}`)

/** Crash-free sessions per variant: dots on the page's scale, the alert as a dashed tick. */
function CrashFreeDots({
  rows,
  scale,
  alert,
  focus,
  words = true,
}: {
  rows: VariantRow[]
  scale: [number, number]
  alert: number | undefined
  /** On one variant's row: the others fade. */
  focus?: number
  words?: boolean
}) {
  const { t } = useI18n()
  const gap = crashFreeGap(rows) >= BAD_GAP
  const lo = Math.min(...rows.flatMap((r) => (r.crashFree === null ? [] : [r.crashFree])))
  const bad = (r: VariantRow) => gap && r.crashFree === lo
  const said = rows.map((r) => `${r.label} ${cfText(r.crashFree)} %`).join(', ')
  return (
    <span className="ff-cf">
      <span
        className="ff-dp"
        role="img"
        aria-label={t(alert === undefined ? 'flags.cfLabel' : 'flags.cfLabelAlert', {
          lo: scale[0].toFixed(2),
          hi: scale[1].toFixed(2),
          alert: alert?.toFixed(2) ?? '',
          values: said,
        })}
      >
        {alert !== undefined ? <span className="ff-al" style={{ left: at(scale, alert) }} /> : null}
        {rows.map((r, i) =>
          r.crashFree === null ? null : (
            <i
              key={r.key || 'other'}
              className={[stepClass(i, r), bad(r) ? 'ff-bad' : ''].join(' ')}
              style={{
                left: at(scale, r.crashFree),
                ...(focus !== undefined && focus !== i ? { opacity: 0.18 } : {}),
              }}
            />
          ),
        )}
      </span>
      {words ? (
        <span className="ff-cft" aria-hidden="true" translate="no">
          {rows.map((r) => (
            <span key={r.key || 'other'} className={bad(r) ? 'c-crash' : undefined}>
              {r.label} <b>{cfText(r.crashFree)}</b>
            </span>
          ))}
        </span>
      ) : null}
    </span>
  )
}

/** The scale's head: its two ends, and the alert only when a rule sets one. */
function ScaleHead({ scale, alert }: { scale: [number, number]; alert: number | undefined }) {
  const { t } = useI18n()
  return (
    <span className="ff-scale" aria-hidden="true">
      <span style={{ left: 0 }}>{scale[0].toFixed(2)}</span>
      {alert !== undefined ? (
        <span className="c-warn" style={{ left: at(scale, alert) }}>
          {t('flags.alert')}
          <span className="ff-an"> {alert.toFixed(2)}</span>
        </span>
      ) : null}
      <span>{scale[1].toFixed(2)}</span>
    </span>
  )
}

function HowFlagsArrive() {
  const { t, rich } = useI18n()
  return (
    <Section title={t('flags.how.title')}>
      <p className="ff-how">
        {rich('flags.how.body', {
          attr: (
            <code className="i-code" translate="no">
              {'feature_flag.<name>'}
            </code>
          ),
        })}
      </p>
      <CodeTabs
        label={t('flags.how.title')}
        tabs={[
          {
            id: 'swift',
            label: 'Swift',
            copy: 'Apsio.setFeatureFlag("new_checkout", variant: "on")',
            code: (
              <span translate="no">
                <span className="cm">{'// after your flag provider resolves'}</span>
                {'\nApsio.setFeatureFlag('}
                <span className="s">"new_checkout"</span>
                {', variant: '}
                <span className="s">"on"</span>
                {')'}
              </span>
            ),
          },
          {
            id: 'kotlin',
            label: 'Kotlin',
            copy: 'Apsio.setFeatureFlag("new_checkout", "on")',
            code: (
              <span translate="no">
                <span className="cm">{'// after your flag provider resolves'}</span>
                {'\nApsio.setFeatureFlag('}
                <span className="s">"new_checkout"</span>
                {', '}
                <span className="s">"on"</span>
                {')'}
              </span>
            ),
          },
        ]}
      />
    </Section>
  )
}

/* ===================== the list ===================== */

export function FlagsPage() {
  const { t, number } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const navigate = useNavigate()
  const { search, days, flags } = useFlags()
  const alert = useCrashFreeAlert(search.app)
  const scopeLine = useScopeLine(search, days)
  const [q, setQ] = useState('')
  const keep = carried(search)
  const params = { orgId, projectId }

  const list = (flags.data?.flags ?? [])
    .map((f) => ({ f, rows: variantRows(t, f) }))
    .sort((a, b) => crashFreeGap(b.rows) - crashFreeGap(a.rows) || b.f.sessions - a.f.sessions)
  const scale = crashFreeScale(
    list.flatMap((x) => x.rows.flatMap((r) => (r.crashFree === null ? [] : [r.crashFree]))),
    alert,
  )
  const term = q.trim().toLowerCase()
  const shown = list.filter(
    (x) =>
      !term ||
      x.f.name.toLowerCase().includes(term) ||
      x.rows.some((r) => !r.other && r.key.toLowerCase().includes(term)),
  )
  const top = list[0]?.f.name
  // The scope's sessions in the same window, every release page (the shell's key), for the
  // exposed share; a flag read after its session started may round it past 100, so it stops there.
  const now = Math.floor(Date.now() / 60_000) * 60_000
  const releases = useQuery(windowReleasesQuery(projectId, days, now))
  const total = (releases.data ?? [])
    .filter((r) => !search.app || r.app_id === search.app)
    .reduce((n, r) => n + r.sessions, 0)

  return (
    <main id="main" className="pg ff-pg">
      <PageHead
        crumb={
          <>
            <Link to="/o/$orgId/p/$projectId/issues" params={params} search={keep}>
              {t('nav.issues')}
            </Link>
            {' / '}
            {t('flags.title')}
          </>
        }
        title={t('flags.title')}
        meta={
          <span>
            {scopeLine}
            {flags.data ? ` · ${t('flags.meta.count', { count: list.length })}` : null}
          </span>
        }
        actions={
          top ? (
            <Link
              className="btn"
              to="/o/$orgId/p/$projectId/flags/$name"
              params={{ ...params, name: top }}
              search={keep}
            >
              {t('flags.open', { name: top })}
            </Link>
          ) : null
        }
      />
      {flags.isPending ? (
        <Loading />
      ) : flags.isError ? (
        <ErrorState error={flags.error} retry={() => flags.refetch()} />
      ) : list.length === 0 ? (
        <>
          <Section title={t('flags.list.title')}>
            <EmptyNote title={t('flags.none', { days })}>{t('flags.noneHint')}</EmptyNote>
          </Section>
          <HowFlagsArrive />
        </>
      ) : (
        <>
          <Section
            className="ff-list"
            title={t('flags.list.title')}
            meta={t('flags.list.meta', { count: list.length })}
            right={
              <span className="ff-tools">
                <input
                  className="field ff-q"
                  type="search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t('flags.search')}
                  aria-label={t('flags.search')}
                />
              </span>
            }
          >
            <Table label={t('flags.list.table')} rowKeys>
              <thead>
                <tr>
                  <th className="f" scope="col">
                    {t('flags.col.flag')}
                  </th>
                  <th className="ff-w" scope="col">
                    {t('flags.col.split')} <span className="faint">{t('flags.col.splitSub')}</span>
                  </th>
                  <th className="num ff-w" scope="col" title={t('flags.col.exposedTitle')}>
                    {t('flags.col.exposed')}
                  </th>
                  <th className="ff-w" scope="col">
                    {t('flags.col.crashFree')}
                    <ScaleHead scale={scale} alert={alert} />
                  </th>
                  <th className="last" scope="col">
                    {t('flags.col.go')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map(({ f, rows }, n) => (
                  <HovRow
                    key={f.name}
                    cur={n === 0 && !term}
                    label={t('flags.openRow', { name: f.name })}
                    onOpen={() =>
                      navigate({
                        to: '/o/$orgId/p/$projectId/flags/$name',
                        params: { ...params, name: f.name },
                        search: keep,
                      })
                    }
                  >
                    <td className="f">
                      <Link
                        className="ttl"
                        translate="no"
                        to="/o/$orgId/p/$projectId/flags/$name"
                        params={{ ...params, name: f.name }}
                        search={keep}
                      >
                        {f.name}
                      </Link>
                      <span className="t2">
                        {t('flags.variants', { count: rows.filter((r) => !r.other).length })}
                      </span>
                    </td>
                    <td>
                      <Rollout rows={rows} labels />
                    </td>
                    <td className="num">
                      {number(f.sessions)}
                      {total ? (
                        <span className="t2">
                          {t('flags.exposedShare', {
                            share: Math.min(100, Math.round((f.sessions / total) * 100)),
                          })}
                        </span>
                      ) : null}
                    </td>
                    <td>
                      <CrashFreeDots rows={rows} scale={scale} alert={alert} />
                    </td>
                    <td className="last ff-go">
                      <Link
                        className="ul"
                        to="/o/$orgId/p/$projectId/issues"
                        params={params}
                        search={{ ...keep, flag: f.name }}
                      >
                        {t('flags.issues')}
                      </Link>
                      <span className="faint" aria-hidden="true">
                        {' · '}
                      </span>
                      <Link
                        className="ul"
                        to="/o/$orgId/p/$projectId/sessions"
                        params={params}
                        search={{ ...keep, flag: f.name }}
                      >
                        {t('flags.sessions')}
                      </Link>
                    </td>
                  </HovRow>
                ))}
                {shown.length === 0 ? (
                  <tr>
                    <td className="f" colSpan={5}>
                      <span className="dim">{t('flags.noMatch')}</span>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </Table>
            <TableFoot>
              <span>{t('flags.foot', { days })}</span>
            </TableFoot>
          </Section>
          <div className="ff-two">
            <HowFlagsArrive />
          </div>
        </>
      )}
    </main>
  )
}

/* ===================== one flag ===================== */

/** Issues listed per variant, the most sessions first: "only here" is among these. */
const ISSUES_PER_VARIANT = 50
/** Variants read for issues (one request each). */
const MAX_ISSUE_VARIANTS = 5

const KIND_ORDER = ['crash', 'anr', 'hang', 'error', 'abnormal_exit'] as const

function kindsLine(t: Translator['t'], issues: IssueSummary[]): string {
  const n = new Map<string, number>()
  for (const i of issues) n.set(i.kind, (n.get(i.kind) ?? 0) + 1)
  return KIND_ORDER.filter((k) => n.get(k))
    .map((k) => t(`flags.kind.${k}`, { count: n.get(k) ?? 0 }))
    .join(', ')
}

export function FlagPage() {
  const i18n = useI18n()
  const { t, number } = i18n
  const { orgId, projectId } = useProjectParams()
  const { name = '' } = useParams({ strict: false }) as { name?: string }
  const { search, days, flags } = useFlags()
  const alert = useCrashFreeAlert(search.app)
  const scopeLine = useScopeLine(search, days)
  const keep = carried(search)
  const params = { orgId, projectId }
  const flag = flags.data?.flags.find((f) => f.name === name)
  const rows = flag ? variantRows(t, flag) : []
  const named = rows.filter((r) => !r.other).slice(0, MAX_ISSUE_VARIANTS)
  const issues = useQueries({
    queries: named.map((r) =>
      issuesQuery(projectId, {
        app_id: search.app,
        flag: name,
        flag_variant: r.key,
        state: 'all',
        sort: 'sessions',
        days,
        limit: ISSUES_PER_VARIANT,
      }),
    ),
  })
  const scale = crashFreeScale(
    rows.flatMap((r) => (r.crashFree === null ? [] : [r.crashFree])),
    alert,
  )
  const share = shares(rows)
  const first = named[0]?.key

  const crumb = (
    <>
      <Link to="/o/$orgId/p/$projectId/issues" params={params} search={keep}>
        {t('nav.issues')}
      </Link>
      {' / '}
      <Link to="/o/$orgId/p/$projectId/flags" params={params} search={keep}>
        {t('flags.title')}
      </Link>
      {' / '}
      <span translate="no">{name}</span>
    </>
  )
  const title = <span translate="no">{name}</span>

  if (flags.isPending)
    return (
      <main id="main" className="pg ff-pg">
        <PageHead crumb={crumb} title={title} variant="mono" meta={scopeLine} />
        <Loading />
      </main>
    )
  if (flags.isError)
    return (
      <main id="main" className="pg ff-pg">
        <PageHead crumb={crumb} title={title} variant="mono" meta={scopeLine} />
        <ErrorState error={flags.error} retry={() => flags.refetch()} />
      </main>
    )
  if (!flag)
    return (
      <main id="main" className="pg ff-pg">
        <PageHead crumb={crumb} title={title} variant="mono" meta={scopeLine} />
        <Section
          title={t('flags.notSeen.title')}
          meta={<span translate="no">{`feature_flag.${name}`}</span>}
        >
          <EmptyNote title={t('flags.notSeen.body', { days })}>
            <Link className="btn" to="/o/$orgId/p/$projectId/flags" params={params} search={keep}>
              {t('flags.all')}
            </Link>
          </EmptyNote>
        </Section>
      </main>
    )

  // Issues per variant, and the ones no other listed variant has.
  const ofVariant = named.map((_, i) => issues[i]?.data?.issues)
  const only = (i: number) => {
    const mine = ofVariant[i]
    if (!mine) return undefined
    const others = new Set(
      ofVariant.flatMap((list, j) => (j === i || !list ? [] : list.map((x) => x.id))),
    )
    return mine.filter((x) => !others.has(x.id))
  }
  const sessionsHref = (variant: string) => ({ ...keep, flag: name, variant })

  return (
    <main id="main" className="pg ff-pg">
      <PageHead
        crumb={crumb}
        title={title}
        variant="mono"
        meta={
          <span>
            <span translate="no">
              {rows
                .filter((r) => !r.other)
                .map((r) => r.label)
                .join(' / ')}
            </span>
            {` · ${scopeLine}`}
          </span>
        }
        actions={
          first !== undefined ? (
            <>
              <Link
                className="btn"
                to="/o/$orgId/p/$projectId/sessions"
                params={params}
                search={sessionsHref(first)}
              >
                {t('flags.sessionsWith', { variant: first })}
              </Link>
              <Link
                className="btn"
                to="/o/$orgId/p/$projectId/issues"
                params={params}
                search={sessionsHref(first)}
              >
                {t('flags.issuesWith', { variant: first })}
              </Link>
            </>
          ) : null
        }
      />
      <Section
        title={t('flags.split.title')}
        meta={t('flags.split.meta', { sessions: number(flag.sessions) })}
      >
        <div
          className="ff-split"
          role="img"
          aria-label={t('flags.splitLabel', {
            split: rows
              .map((r, i) => `${r.label} ${Math.round(share[i] ?? 0)} %, ${number(r.sessions)}`)
              .join('; '),
          })}
        >
          <div className="ff-sr ff-top" aria-hidden="true" translate="no">
            {rows.map((r, i) => (
              <span key={r.key || 'other'} style={{ flex: `${share[i]} 1 0` }}>
                <b>{r.label}</b> {Math.round(share[i] ?? 0)} %
              </span>
            ))}
          </div>
          <div className="ff-sb">
            {rows.map((r, i) => (
              <i
                key={r.key || 'other'}
                className={stepClass(i, r)}
                style={{ flex: `${share[i]} 1 0` }}
              />
            ))}
          </div>
          <div className="ff-sr" aria-hidden="true">
            {rows.map((r, i) => (
              <span key={r.key || 'other'} style={{ flex: `${share[i]} 1 0` }}>
                {t('flags.split.sessions', { sessions: number(r.sessions) })}
              </span>
            ))}
          </div>
        </div>
        <Table label={t('flags.split.table')} className="ff-vt">
          <thead>
            <tr>
              <th className="f" scope="col">
                {t('flags.col.variant')}
              </th>
              <th className="ff-w" scope="col">
                {t('flags.col.crashFree')}
                <ScaleHead scale={scale} alert={alert} />
              </th>
              <th className="num" scope="col">
                {t('flags.col.crashFreeShort')}
              </th>
              <th scope="col">{t('flags.col.issues')}</th>
              <th className="last" scope="col" title={t('flags.col.onlyTitle')}>
                {t('flags.col.only')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const others = rows.filter((x, j) => j !== i && x.crashFree !== null)
              const ref =
                others.length && r.crashFree !== null
                  ? others.reduce((n, x) => n + (x.crashFree ?? 0), 0) / others.length
                  : undefined
              const dv = ref === undefined || r.crashFree === null ? undefined : r.crashFree - ref
              const vs = others.length === 1 ? (others[0]?.label ?? '') : t('flags.others')
              const vi = named.findIndex((x) => x.key === r.key && !r.other)
              const list = vi >= 0 ? ofVariant[vi] : undefined
              const mine = vi >= 0 ? only(vi) : undefined
              const pending = vi >= 0 && issues[vi]?.isPending
              return (
                <tr key={r.key || 'other'}>
                  <td className="f">
                    <span className={`ff-sw ${stepClass(i, r)}`} aria-hidden="true" />
                    <span className="ttl" translate="no">
                      {r.label}
                    </span>
                    <span className="t2">{Math.round(share[i] ?? 0)} %</span>
                  </td>
                  <td className="ff-dpc">
                    <CrashFreeDots
                      rows={rows}
                      scale={scale}
                      alert={alert}
                      focus={i}
                      words={false}
                    />
                  </td>
                  <td className="num">
                    {r.crashFree === null ? '—' : `${cfText(r.crashFree)} %`}
                    <span className="t2">
                      {dv === undefined
                        ? ''
                        : Math.abs(dv) < 0.005
                          ? t('flags.same', { vs })
                          : t('flags.vs', {
                              delta: `${dv > 0 ? '+' : '−'}${Math.abs(dv).toFixed(2)}`,
                              vs,
                            })}
                    </span>
                  </td>
                  <td className="wrap ff-ik">
                    {r.other ? (
                      <span className="faint">—</span>
                    ) : pending ? (
                      <span className="faint">{t('state.loading')}</span>
                    ) : list ? (
                      <>
                        {number(list.length)}
                        {list.length >= ISSUES_PER_VARIANT ? '+' : ''}
                        <span className="t2">{kindsLine(t, list) || t('flags.none0')}</span>
                      </>
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                  <td className="last">
                    {mine === undefined ? (
                      <span className="faint">—</span>
                    ) : mine.length ? (
                      mine.slice(0, 3).map((x) => (
                        <Link
                          key={x.id}
                          className={LK}
                          to="/o/$orgId/p/$projectId/issues/$issueId"
                          params={{ ...params, issueId: x.id }}
                          search={keep}
                          translate="no"
                          title={issueTitle(x)}
                          aria-label={`${issueTitle(x)}, ${x.id}`}
                        >
                          {x.id}
                        </Link>
                      ))
                    ) : (
                      <span className="faint">{t('flags.none0')}</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
        <TableFoot>
          <span>{t('flags.split.foot', { count: ISSUES_PER_VARIANT })}</span>
        </TableFoot>
      </Section>
      <div className="ff-two">
        <Section title={t('flags.about.title')}>
          <KV
            className="ff-kv"
            items={[
              [
                t('flags.about.attribute'),
                <span key="a" translate="no">{`feature_flag.${name}`}</span>,
              ],
              [
                t('flags.about.variants'),
                <span key="v" translate="no">
                  {rows.map((r, i) => `${r.label} ${Math.round(share[i] ?? 0)} %`).join(' · ')}
                </span>,
              ],
              [
                t('flags.about.exposed'),
                t('flags.split.sessions', { sessions: number(flag.sessions) }),
              ],
            ]}
          />
        </Section>
        <Section title={t('flags.go.title')}>
          <ul className="rl ff-go">
            {named.map((r) => (
              <li key={r.key}>
                <span>
                  <Link
                    to="/o/$orgId/p/$projectId/sessions"
                    params={params}
                    search={sessionsHref(r.key)}
                  >
                    {t('flags.sessionsWith', { variant: r.key })}
                  </Link>
                </span>
                <span>{number(r.sessions)}</span>
              </li>
            ))}
            {named.map((r) => (
              <li key={`i-${r.key}`}>
                <span>
                  <Link
                    to="/o/$orgId/p/$projectId/issues"
                    params={params}
                    search={sessionsHref(r.key)}
                  >
                    {t('flags.issuesWith', { variant: r.key })}
                  </Link>
                </span>
                <span />
              </li>
            ))}
          </ul>
        </Section>
      </div>
    </main>
  )
}
